import * as React from "react";
import { Archive, ArrowUp, Globe, HelpCircle } from "lucide-react";
import { Button, Kbd, SimpleSelect, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ActorRef, DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { WhatCanIAskDialog } from "../help/WhatCanIAskDialog";
import { slideKindLabel, slideTitle } from "../slideState";
import { ActorAvatar } from "./ActivityPanel";
import { ARCHIVED_NOTICE, GLOBAL_NOTICE, ReadOnlyNotice } from "./ReadOnlyNotice";

export type ComposerState =
  | { kind: "idle"; candidatePending: boolean }
  | { kind: "sending" }
  | { kind: "disabled_in_flight"; actor: ActorRef; itemIds: number[]; isSelf: boolean }
  | { kind: "disabled_read_only" }
  | { kind: "disabled_archived" }
  | { kind: "disabled_global" };

export interface SendOptions {
  /** Start from the kept version instead of building on the pending one. */
  fromKept: boolean;
}

interface RefinementComposerProps {
  pkg: DesignPackageDetail;
  selectedItemId: number;
  state: ComposerState;
  onSend: (instructions: string, scope: "all" | number, options: SendOptions) => Promise<void>;
  prefill?: { text: string; nonce: number };
}

const MAX_LENGTH = 1000;
const COUNTER_FROM = 900;

type SlideKind = ReturnType<typeof slideKindLabel>;

// A few things worth asking for on each kind of slide; clicking one fills the box.
const SUGGESTIONS: Record<SlideKind | "all", string[]> = {
  Review: ["Make the quote bigger", "Use warmer colors", "Put the stars above the quote"],
  Intro: ["Make the headline bolder", "Add more space around the text", "Use a brand color background"],
  Ending: ["Make the call to action stand out", "Add the player's headshot", "Simplify the layout"],
  Profile: ["Make the headshot bigger", "Use warmer colors", "Move the name above the photo"],
  Slide: ["Make the headline bigger", "Use warmer colors", "Move the headshot to the left"],
  all: ["Use warmer colors on every slide", "Make the text bigger everywhere", "Use one brand color throughout"],
};

function inFlightLine(pkg: DesignPackageDetail, state: Extract<ComposerState, { kind: "disabled_in_flight" }>): string {
  const whole = state.itemIds.length !== 1 && pkg.items.length > 1;
  const position = pkg.items.find(i => i.id === state.itemIds[0])?.position;
  const slide = pkg.items.length === 1 || position == null ? "the design" : `slide ${position}`;
  if (state.isSelf) return `Refining ${whole ? "all slides" : slide}… about a minute.`;
  if (whole) return `${state.actor.name} is refining all slides…`;
  return `${state.actor.name} is refining ${slide}…`;
}

const isTouchKeyboard = () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

export const RefinementComposer: React.FC<RefinementComposerProps> = ({
  pkg,
  selectedItemId,
  state,
  onSend,
  prefill,
}) => {
  const [text, setText] = React.useState("");
  const [scope, setScope] = React.useState<"all" | number>("all");
  const [fromKept, setFromKept] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const items = React.useMemo(() => [...pkg.items].sort((a, b) => a.position - b.position), [pkg.items]);
  const multiSlide = items.length > 1;

  // A slide click after mount means the coach is looking at that slide; follow it.
  const initialSelection = React.useRef(selectedItemId);
  React.useEffect(() => {
    if (selectedItemId !== initialSelection.current) {
      initialSelection.current = selectedItemId;
      if (multiSlide) setScope(selectedItemId);
    }
  }, [selectedItemId, multiSlide]);

  // Keyed on the nonce so the same text can be applied twice.
  const prefillRef = React.useRef(prefill);
  prefillRef.current = prefill;
  React.useEffect(() => {
    if (!prefillRef.current) return;
    setText(prefillRef.current.text);
    textareaRef.current?.focus();
  }, [prefill?.nonce]);

  const effectiveScope = multiSlide ? scope : "all";
  const scopedItem = effectiveScope === "all" ? null : items.find(i => i.id === effectiveScope) ?? null;
  // The next change to one slide builds on its pending version unless the coach asks to start from
  // the kept one. A change to every slide always starts from the kept versions, so there's no choice.
  const pendingVersion =
    state.kind === "idle" && state.candidatePending && (scopedItem ? !!scopedItem.candidateTemplate : !multiSlide);
  React.useEffect(() => setFromKept(false), [effectiveScope]);

  // The page shows the read-only notice under the header; repeating it here adds nothing.
  if (state.kind === "disabled_read_only") return null;
  if (state.kind === "disabled_archived") return <ReadOnlyNotice {...ARCHIVED_NOTICE} icon={Archive} />;
  if (state.kind === "disabled_global") return <ReadOnlyNotice {...GLOBAL_NOTICE} icon={Globe} />;

  if (state.kind === "disabled_in_flight") {
    return (
      <div
        className="flex items-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
        data-testid="refinement-composer"
      >
        {state.isSelf ? (
          <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        ) : (
          <ActorAvatar actor={state.actor} />
        )}
        <p className="text-sm font-medium text-slate-700" data-testid="composer-in-flight">
          {inFlightLine(pkg, state)}
        </p>
      </div>
    );
  }

  const sending = state.kind === "sending";
  const tooLong = text.length > MAX_LENGTH;
  const canSend = !sending && !tooLong && text.trim().length > 0;
  const kind: SlideKind | "all" = scopedItem
    ? slideKindLabel(pkg, scopedItem)
    : multiSlide
    ? "all"
    : slideKindLabel(pkg, items[0]);
  const scopedPosition = scopedItem?.position ?? null;

  const send = async () => {
    if (!canSend) return;
    try {
      await onSend(text.trim(), multiSlide ? effectiveScope : items[0]?.id ?? "all", { fromKept });
      setText("");
    } catch {
      // The mutation hook toasts; the text stays so the coach can try again.
    }
  };

  const scopeItems = [
    { value: "all", label: "All slides" },
    ...items.map(item => ({
      value: String(item.id),
      label: slideTitle(item.position, slideKindLabel(pkg, item)),
    })),
  ];

  return (
    <div className="flex flex-col gap-2" data-testid="refinement-composer">
      {state.kind === "idle" && pendingVersion && (
        <p className="text-xs text-slate-500" data-testid="composer-base">
          {fromKept ? "Starting from the kept version" : "Building on the new version"} ·{" "}
          <Button variant="link" size="inline" className="text-xs" onClick={() => setFromKept(!fromKept)}>
            {fromKept ? "Build on the new version instead" : "Start from the kept one instead"}
          </Button>
        </p>
      )}

      <div
        className={cn(
          "flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm transition-shadow",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20",
          tooLong && "border-destructive"
        )}
      >
        {multiSlide && (
          <div className="flex items-center gap-1.5 px-1 text-xs" data-testid="composer-scope">
            <span className="text-slate-500">Changing</span>
            <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 font-semibold text-primary">
              <SimpleSelect
                items={scopeItems}
                value={String(scope)}
                onValueChange={value => setScope(!value || value === "all" ? "all" : Number(value))}
                disabled={sending}
                className="text-xs"
              />
            </span>
          </div>
        )}
        <Textarea
          ref={textareaRef}
          autoSize
          minRows={2}
          maxRows={6}
          value={text}
          disabled={sending}
          aria-label="Describe a change"
          aria-invalid={tooLong ? true : undefined}
          placeholder={scopedPosition != null ? `Describe a change to slide ${scopedPosition}…` : "Describe a change…"}
          className="border-0 shadow-none focus-visible:ring-0"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key !== "Enter" || e.shiftKey) return;
            // Enter confirms a character while typing with an input method (Japanese, Chinese, …).
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            // On touch keyboards Enter adds a line, as people expect there; the Send button sends.
            if (!(e.metaKey || e.ctrlKey) && isTouchKeyboard()) return;
            e.preventDefault();
            void send();
          }}
        />
        <div className="flex items-center gap-2 px-1 text-xs text-slate-400">
          <Button variant="ghost" size="icon-xs" aria-label="What can I ask for?" onClick={() => setHelpOpen(true)}>
            <HelpCircle aria-hidden="true" />
          </Button>
          {text.length >= COUNTER_FROM && (
            <span className={tooLong ? "text-destructive" : undefined}>
              {text.length.toLocaleString()}/{MAX_LENGTH.toLocaleString()}
            </span>
          )}
          <span className="ml-auto hidden items-center gap-1 md:inline-flex">
            <Kbd>↵</Kbd> to send · <Kbd>⇧↵</Kbd> new line
          </span>
          <Button size="icon-sm" aria-label="Send" loading={sending} disabled={!canSend} onClick={() => void send()}>
            <ArrowUp aria-hidden="true" />
          </Button>
        </div>
      </div>

      {tooLong && <p className="text-xs text-destructive">Keep it under 1,000 characters.</p>}

      {text.length === 0 && !sending && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="composer-suggestions">
          <span className="text-xs text-slate-500">Try:</span>
          {SUGGESTIONS[kind].map(suggestion => (
            <Button
              key={suggestion}
              variant="outline"
              size="xs"
              className="rounded-full"
              onClick={() => {
                setText(suggestion);
                textareaRef.current?.focus();
              }}
            >
              {suggestion}
            </Button>
          ))}
        </div>
      )}
      <WhatCanIAskDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
};
